'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Saint {
  id: string;
  name: string;
  feastDay: string;
  patronOf: string[];
  image: string;
  alt: string;
}

const featuredSaints: Saint[] = [
{
  id: 'st_francis',
  name: 'St. Francis of Assisi',
  feastDay: 'October 4',
  patronOf: ['Animals', 'Ecology', 'Merchants'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_117aab0ae-1765689274966.png",
  alt: 'St. Francis of Assisi in brown robes with birds in forest setting'
},
{
  id: 'st_teresa',
  name: 'St. Teresa of Ávila',
  feastDay: 'October 15',
  patronOf: ['Writers', 'Spain', 'Headaches'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_136c9763e-1764748804444.png",
  alt: 'St. Teresa of Ávila in contemplative pose with book'
},
{
  id: 'st_joseph',
  name: 'St. Joseph',
  feastDay: 'March 19',
  patronOf: ['Workers', 'Fathers', 'Carpenters'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_154863238-1764689619733.png",
  alt: 'St. Joseph with carpenter tools and gentle expression'
}];


export default function HeroSection() {
  const [saintCount, setSaintCount] = useState(0);
  const targetCount = 1000;

  useEffect(() => {
    const duration = 2000;
    const increment = targetCount / (duration / 16);
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= targetCount) {
        setSaintCount(targetCount);
        clearInterval(timer);
      } else {
        setSaintCount(Math.floor(current));
      }
    }, 16);

    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-32 pb-20 px-6 overflow-hidden">
      {/* Background Glow */}
      <div className="absolute inset-0 bg-gradient-radial from-primary/5 via-transparent to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column: Text Content */}
          <div className="space-y-8 reveal">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 glass-card px-4 py-2 rounded-full border border-primary/20">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              <span className="text-xs font-medium text-primary uppercase tracking-wider">
                Daily Saint Updates
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-foreground leading-tight">
              Discover Your{' '}
              <span className="text-primary">Patron Saint</span>
            </h1>

            {/* Subheadline */}
            <p className="text-xl text-muted-foreground leading-relaxed max-w-xl">
              Search over 1,000 Catholic saints by name, patronage, or feast day. Learn their stories, pray their prayers, and shop meaningful merchandise.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/search-saints"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-xl text-base font-semibold hover:bg-primary/90 transition-all hover:-translate-y-0.5 shadow-lg hover:shadow-xl">
                
                <Icon name="MagnifyingGlassIcon" size={20} />
                <span>Search Saints</span>
              </Link>
              <Link
                href="/saint-of-the-day"
                className="inline-flex items-center justify-center gap-2 bg-card text-foreground border-2 border-border px-8 py-4 rounded-xl text-base font-semibold hover:border-primary hover:text-primary transition-all">
                
                <Icon name="CalendarIcon" size={20} />
                <span>Saint of the Day</span>
              </Link>
            </div>

            {/* Stats */}
            <div className="pt-8 border-t border-border">
              <div className="flex items-center gap-6">
                <div>
                  <div className="text-4xl font-bold text-primary font-serif">
                    {saintCount.toLocaleString()}+
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Catholic Saints
                  </div>
                </div>
                <div className="h-12 w-px bg-border" />
                <div>
                  <div className="text-4xl font-bold text-accent font-serif">
                    365
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Feast Days
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Floating Saint Cards */}
          <div className="relative h-[600px] hidden lg:block reveal">
            <div className="absolute inset-0">
              {/* Card 1 - Top Left */}
              <div className="absolute top-0 left-0 w-64 glass-card p-4 rounded-2xl shadow-xl animate-float">
                <div className="aspect-[3/4] overflow-hidden rounded-xl mb-3">
                  <AppImage
                    src={featuredSaints[0].image}
                    alt={featuredSaints[0].alt}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                  
                </div>
                <h3 className="font-serif font-semibold text-foreground mb-1">
                  {featuredSaints[0].name}
                </h3>
                <p className="text-xs text-muted-foreground mb-2">
                  {featuredSaints[0].feastDay}
                </p>
                <div className="flex flex-wrap gap-1">
                  {featuredSaints[0].patronOf.slice(0, 2).map((patron) =>
                  <span
                    key={patron}
                    className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                    
                      {patron}
                    </span>
                  )}
                </div>
              </div>

              {/* Card 2 - Top Right */}
              <div className="absolute top-12 right-0 w-64 glass-card p-4 rounded-2xl shadow-xl animate-float-delayed">
                <div className="aspect-[3/4] overflow-hidden rounded-xl mb-3">
                  <AppImage
                    src={featuredSaints[1].image}
                    alt={featuredSaints[1].alt}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                  
                </div>
                <h3 className="font-serif font-semibold text-foreground mb-1">
                  {featuredSaints[1].name}
                </h3>
                <p className="text-xs text-muted-foreground mb-2">
                  {featuredSaints[1].feastDay}
                </p>
                <div className="flex flex-wrap gap-1">
                  {featuredSaints[1].patronOf.slice(0, 2).map((patron) =>
                  <span
                    key={patron}
                    className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                    
                      {patron}
                    </span>
                  )}
                </div>
              </div>

              {/* Card 3 - Bottom Center */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-72 glass-card p-4 rounded-2xl shadow-2xl">
                <div className="aspect-[3/4] overflow-hidden rounded-xl mb-3">
                  <AppImage
                    src={featuredSaints[2].image}
                    alt={featuredSaints[2].alt}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                  
                </div>
                <h3 className="font-serif font-semibold text-foreground mb-1">
                  {featuredSaints[2].name}
                </h3>
                <p className="text-xs text-muted-foreground mb-2">
                  {featuredSaints[2].feastDay}
                </p>
                <div className="flex flex-wrap gap-1">
                  {featuredSaints[2].patronOf.slice(0, 2).map((patron) =>
                  <span
                    key={patron}
                    className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                    
                      {patron}
                    </span>
                  )}
                </div>
              </div>

              {/* Decorative Glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
            </div>
          </div>
        </div>
      </div>
    </section>);

}